import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Send, ArrowLeft, MessageSquare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingState, EmptyState } from "@/components/States";
import { timeAgo } from "@/lib/format";
import { imageUrl } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const selectedId = params.get("c");

  const loadConvs = () => api.get("/conversations").then(({ data }) => setConvs(data.items));
  useEffect(() => { loadConvs(); }, []);

  useEffect(() => {
    if (!selectedId) { setActive(null); setMessages([]); return; }
    api.get(`/conversations/${selectedId}`).then(({ data }) => {
      setActive(data.conversation); setMessages(data.messages);
    });
  }, [selectedId]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  // Real-time polling: refresh thread + conversation list
  useEffect(() => {
    const interval = setInterval(() => {
      loadConvs();
      if (selectedId) {
        api.get(`/conversations/${selectedId}`)
          .then(({ data }) => setMessages((prev) => (data.messages.length !== prev.length ? data.messages : prev)))
          .catch(() => {});
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedId]);

  const open = (id) => setParams({ c: id });

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${selectedId}/messages`, { text });
      setMessages((m) => [...m, data.message]);
      setText("");
      loadConvs();
    } finally { setSending(false); }
  };

  if (convs === null) return <LoadingState />;

  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Mesajlar</h1>
      <div className="grid overflow-hidden rounded-xl border border-slate-200 bg-white md:grid-cols-[320px_1fr]" style={{ height: "70vh" }}>
        {/* List */}
        <div className={cn("flex flex-col border-r border-slate-200", selectedId && "hidden md:flex")}>
          {convs.length === 0 ? (
            <div className="flex flex-1 items-center justify-center p-6"><EmptyState title="Mesaj yok" icon={MessageSquare} /></div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {convs.map((c) => (
                <button key={c.conversation_id} onClick={() => open(c.conversation_id)} data-testid={`conv-${c.conversation_id}`}
                  className={cn("flex w-full items-center gap-3 border-b border-slate-100 p-3 text-left hover:bg-slate-50",
                    selectedId === c.conversation_id && "bg-emerald-50")}>
                  <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                    {c.listing?.image && <img src={imageUrl(c.listing.image)} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800">{c.other_user?.name}</p>
                    <p className="truncate text-xs text-slate-500">{c.listing?.title}</p>
                    <p className="truncate text-xs text-slate-400">{c.last_message}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Thread */}
        <div className={cn("flex flex-col", !selectedId && "hidden md:flex")}>
          {!active ? (
            <div className="flex flex-1 items-center justify-center text-sm text-slate-400">Bir konuşma seçin</div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-slate-200 p-3">
                <button onClick={() => setParams({})} className="md:hidden"><ArrowLeft size={20} className="text-slate-500" /></button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{active.other_user?.name}</p>
                  <Link to={`/ilan/${active.listing_id}`} className="truncate text-xs text-emerald-600 hover:underline">{active.listing?.title}</Link>
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                      <div className={cn("max-w-[75%] rounded-2xl px-3.5 py-2 text-sm",
                        mine ? "bg-emerald-600 text-white" : "border border-slate-200 bg-white text-slate-800")}>
                        <p className="whitespace-pre-line">{m.text}</p>
                        <p className={cn("mt-1 text-[10px]", mine ? "text-emerald-100" : "text-slate-400")}>{timeAgo(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-slate-200 p-3">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Mesaj yazın..." data-testid="message-input"
                  className="focus-visible:ring-emerald-500" />
                <Button type="submit" size="icon" disabled={sending} data-testid="message-send" className="bg-emerald-600 hover:bg-emerald-700">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send size={18} />}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
